import camelCase from 'lodash.camelcase';
import * as React from 'react';

import { EventCustomFeedbackCodes, EventFeedbackCodes, EventTypes, getEventImageSrc } from 'defs/events';
import { EventDto, EventDtoEnhanced } from 'defs/events';
import { TargetActions, TargetTypes } from 'defs/notifications';
import { get as getBadgeTargets } from 'defs/notifications/badge';
import { get as getFeedbackTargets } from 'defs/notifications/feedback';
import { get as getImageTargets } from 'defs/notifications/image';
import { get as getLevelUpTargets, levelLabels } from 'defs/notifications/levelUp';
import { get as getStickerTargets } from 'defs/notifications/sticker';
import { get as getWarningTargets } from 'defs/notifications/warning';

import { warn } from 'helper/debug';
import i18n, { labelExists } from 'helper/i18n';
import { template } from 'helper/string';
import { reload } from 'lib/ApplicationLoader';

import Checkbox from 'components/atoms/Checkbox';
import Coach from 'components/atoms/Coach';
import UIAsset from 'components/atoms/UIAsset';

import FameLevel from 'components/molecules/Fame/Level';
import { LinkListEntryType, LinkListProps } from 'components/molecules/LinkList';
import LinkList from 'components/molecules/LinkList';

import { Radio } from 'components/atoms/Radio/Radio';
import { NotificationContainerProps } from 'containers/molecules/Notification';
import ProgramSummary from 'containers/molecules/Program/ProgramSummary';
import { CoachType } from 'types/coach';
import { TargetType, TargetTypePrimary, TargetTypeSecondary } from 'types/NotificationTargets';

export type NotificationOverwrites = {
	title?: string;
	text?: string;
	image?: string;
	illustrationMarkup?: React.ReactNode;
	coach?: CoachType;
	targets?: TargetType;
};

export type NotificationOwnPropsType = EventDto &
	EventDtoEnhanced & {
		callback?: Function;
		callbackId?: string;
		modifier?: string;
		onCloseNotification: Function;
		infoText?: string;
		overwrites?: NotificationOverwrites;
		fameBadge?: React.ReactNode;
	};
export type NotificationPropsType = NotificationContainerProps & NotificationOwnPropsType;

type NotificationStateType = {
	hasAcceptedCompetition: boolean;
	dsgvo: 'YES' | 'NO' | null;
};

export default class Notification extends React.Component<NotificationPropsType, NotificationStateType> {
	get defaultBaseLabel() {
		return `react.impulsCoach.events.${camelCase(this.props.eventType)}.${camelCase(this.props.code)}.${camelCase(
			this.props.eventLevel
		)}`;
	}

	public static displayName = 'Notification';

	public static defaultProps = {
		coach: 'LEO' as 'LEO',
	};

	constructor(props: NotificationPropsType) {
		super(props);

		this.state = {
			hasAcceptedCompetition: this.props.participateCompetition,
			dsgvo: null,
		};
	}

	public tryCallCustomCallback(event: string = 'default', close: () => void) {
		const customCallback = this.props.callback || null;

		if (customCallback) {
			customCallback(event, close);
		} else if (event === 'default') {
			close();
		}
	}

	public componentWillUnmount() {
		this.tryCallCustomCallback('unmount', this.onClose.bind(this));
	}

	public onClose(): void {
		this.props.onCloseNotification(this.props.reactId);
	}

	public onChangeAcceptedCompetition(checked: boolean) {
		this.setState({
			hasAcceptedCompetition: checked,
		});
	}

	public renderNotification() {
		const { modifier } = this.props;

		return (
			<div className={`m-notification${modifier ? ` m-notification--${modifier}` : ''}`}>
				{this.renderNotificationLevelBg()}
				<div className="m-notification__bg">
					<div className="m-notification__figure">
						<svg
							className="m-notification__triangle"
							xmlns="http://www.w3.org/2000/svg"
							aria-hidden="true"
							role="presentation"
							viewBox="0 0 284 60.55"
							preserveAspectRatio="xMinYMin"
						>
							<g>
								<g>
									<polygon fill="none" points="0 29.77 284 0 284 60.55 0 60.55 0 29.77" />
									<polygon
										className="m-notification__triangle-right"
										points="0 10.33 284 33.62 284 58 0 58 0 10.33"
									/>
									<polygon
										className="m-notification__triangle-left"
										points="0 29.77 284 0 284 58 0 58 0 29.77"
									/>
								</g>
								<polygon
									className="m-notification__triangle-top"
									points="0 29.1 104.77 19 284 33.97 284 60.55 0 60.55 0 29.1"
								/>
							</g>
						</svg>
					</div>
				</div>
				{this.renderNotificationVisual()}
				{this.renderNotificationContent()}
			</div>
		);
	}

	public renderNotificationVisual() {
		const { code, eventType, eventLevel, attributes, overwrites = {}, fameBadge, coach } = this.props;
		const inner: any[] = [];

		if (eventType === EventTypes.LevelUp && attributes && attributes.level) {
			inner.push(
				<div key="level" className="m-notification__level">
					<FameLevel level={attributes.level} modifier={'notification'} />
				</div>
			);
		} else if (fameBadge) {
			inner.push(
				<div key="fameBadge" className="m-notification__img">
					{fameBadge}
				</div>
			);
		} else if (overwrites && overwrites.illustrationMarkup) {
			/*
			 * TODO: Eventually remove if Base64 works
			 * Overlays must support direct SVG injection because of the
			 * offline notification.
			 * @author Jan Biasi < jan.biasi@namics.com>
			 * @see https:// jira.migros.net/browse/PULSWM-2663
			 */
			inner.push(overwrites.illustrationMarkup);
		} else {
			const img =
				overwrites && overwrites.image
					? overwrites.image
					: getEventImageSrc(
							code || '',
							eventType,
							eventLevel,
							typeof overwrites.coach === 'string' ? overwrites.coach : coach
					  );

			if (img) {
				inner.push(<img key="image" className="m-notification__img" src={img} alt="" />);
			}
		}

		return <div className="m-notification__visual">{inner}</div>;
	}

	public renderInfo() {
		const { infoText } = this.props;
		return infoText && <span className="m-notification__info">{infoText}</span>;
	}

	public renderCheckbox(label: string) {
		return (
			<div className="m-notification__text">
				<Checkbox
					id="acceptedCompetition"
					tag="div"
					name="acceptedCompetition"
					label={label}
					isChecked={this.state.hasAcceptedCompetition}
					onChange={(checked) => this.onChangeAcceptedCompetition(checked)}
				/>
			</div>
		);
	}

	public renderDsgvoRadios(label: string) {
		return (
			<React.Fragment>
				<p className="m-notification__text">{label}</p>
				<div className="m-notification__text">
					<Radio
						hasBrightBackground
						checked={this.state.dsgvo === 'YES'}
						tag="div"
						id={'dsgvo-yes'}
						name={'dsgvo'}
						value={'YES'}
						label={i18n(`react.impulsCoach.events.feedback.dsgvo.na.radioYes.label`)}
						onChange={(value) => this.setState({ dsgvo: value as 'YES' })}
					/>
				</div>
				<div className="m-notification__text">
					<Radio
						hasBrightBackground
						checked={this.state.dsgvo === 'NO'}
						tag="div"
						id={'dsgvo-no'}
						name={'dsgvo'}
						value={'NO'}
						label={i18n(`react.impulsCoach.events.feedback.dsgvo.na.radioNo.label`)}
						onChange={(value) => this.setState({ dsgvo: value as 'YES' })}
					/>
				</div>
			</React.Fragment>
		);
	}

	public renderCoach() {
		const { coach, eventType } = this.props;

		if (!coach) {
			return null;
		}

		if (eventType === EventTypes.LevelUp) {
			return (
				<div className="m-notification__coach">
					<Coach coach={coach} gesture="front-perfect" alt={coach} />
				</div>
			);
		}

		return null;
	}

	public renderNotificationLevelBg() {
		const { eventType } = this.props;

		if (eventType === EventTypes.LevelUp) {
			return <UIAsset className="m-notification__level-bg" src={`/img/svg/notification/level/bg.svg`} alt="" />;
		}

		return null;
	}

	public renderNotificationContent() {
		const { attributes, overwrites, code } = this.props;
		let tempAttributes: any = { ...attributes };
		const linkList = this.getLinkList();
		const hasTitle = labelExists(`${this.defaultBaseLabel}.title`);
		const hasText = labelExists(`${this.defaultBaseLabel}.text`);

		let title =
			overwrites && overwrites.title ? overwrites.title : hasTitle ? i18n(`${this.defaultBaseLabel}.title`) : '';

		let text: string =
			overwrites && overwrites.text ? overwrites.text : hasText ? i18n(`${this.defaultBaseLabel}.text`) : '';

		if (attributes) {
			if (tempAttributes.level) {
				tempAttributes = {
					...tempAttributes,
					level: levelLabels[tempAttributes.level],
				};
			}

			title = template(title, tempAttributes);
			text = template(text, tempAttributes);
		}

		let element: React.ReactNode | undefined;

		if (code === EventFeedbackCodes.F06) {
			element = this.renderCheckbox(text);
		}

		if (code === EventCustomFeedbackCodes.Dsgvo || code === EventCustomFeedbackCodes.DsgvoReminder) {
			element = this.renderDsgvoRadios(text);
		}

		return (
			<div className="m-notification__content">
				{this.renderCoach()}
				<h2 className="m-notification__title" dangerouslySetInnerHTML={{ __html: title }} />
				{this.renderInfo()}
				{element ? element : <div className="m-notification__text">{text}</div>}
				<div className="m-notification__links">
					{this.renderLoginButton()}
					{linkList && <LinkList links={linkList.links} modifier={linkList.modifier} />}
				</div>
			</div>
		);
	}

	public getLinkList(): LinkListProps | void {
		const links = this.getButtons();

		if (links && this.isDsgvo && !this.state.dsgvo) {
			links[0].disabled = true;
		}

		return links
			? {
					modifier: '',
					links,
			  }
			: void 0;
	}

	public renderLoginButton = () => {
		if (typeof this.props.code !== 'string') {
			return null;
		}

		const targets = this.getNotificationTargets(this.props.eventType, this.props.code) as any;

		if (targets && targets.primary && targets.primary.type === TargetTypes.LoginButton) {
			return; // <LoginButton bigButton />;
			// return <LoginButton bigButton />;
		}

		return null;
	};

	public getNotificationTargets(eventType: string, code: string): TargetType | void {
		// Custom Overlays have dynamic Targets
		if (code === EventCustomFeedbackCodes.CustomOverlay) {
			return this.props.overwrites && this.props.overwrites.targets ? this.props.overwrites.targets : void 0;
		}

		switch (eventType) {
			case EventTypes.Feedback:
				return getFeedbackTargets(code);
			case EventTypes.Warning:
				return getWarningTargets(code);
			case EventTypes.Badge:
				return getBadgeTargets(code);
			case EventTypes.Image:
				return getImageTargets(code);
			case EventTypes.Sticker:
				return getStickerTargets(code);
			case EventTypes.LevelUp:
				return getLevelUpTargets(code);
			default:
				return void 0;
		}
	}

	public onAction(actionName: string): void {
		switch (actionName) {
			case TargetActions.OverwriteProgram:
				const programIdOverride = this.props.payload ? this.props.payload.programId : '';
				this.props.doOverwriteProgram(programIdOverride, this.props.payload.previewDate);
				break;
			case TargetActions.StartProgram:
				const programId = this.props.payload ? this.props.payload.programId : '';
				this.props.doStartProgram(programId, this.props.payload.previewDate);
				break;
			case TargetActions.ReplaceProgram:
				const programIdReplace = this.props.payload ? this.props.payload.programId : '';
				this.props.doReplaceProgram(programIdReplace, this.props.payload.previewDate);
				break;
			case TargetActions.SaveParticipateCompetition:
				this.props.doSaveParticipateCompetition(this.state.hasAcceptedCompetition);
				break;
			case TargetActions.SaveDsgvo:
				this.props.doSaveDsgvo({
					dsgvo: this.state.dsgvo === 'YES',
				});
				break;
			default:
				warn(`actionName ${actionName} is not defined.`);
				return void 0;
		}

		this.onClose();
	}

	public onReload(): void {
		reload();
	}

	public getButtons = (): LinkListEntryType[] | void => {
		const linkEntries: LinkListEntryType[] = [];

		// return if we don't have a valid notification code
		if (typeof this.props.code !== 'string') {
			return;
		}

		// get primary and secondary targets
		const notificationTargets = this.getNotificationTargets(this.props.eventType, this.props.code);
		let primaryTarget = notificationTargets && notificationTargets.primary ? notificationTargets.primary : null;
		let secondaryTarget =
			notificationTargets && notificationTargets.secondary ? notificationTargets.secondary : null;

		// merge overwrites into the targets
		if (this.props.overwrites && this.props.overwrites.targets) {
			if (this.props.overwrites.targets.primary) {
				primaryTarget = {
					...(primaryTarget ? primaryTarget : {}),
					...this.props.overwrites.targets.primary,
				};
			}
			if (this.props.overwrites.targets.secondary) {
				secondaryTarget = {
					...(secondaryTarget ? secondaryTarget : {}),
					...this.props.overwrites.targets.secondary,
				};
			}
		}

		if (primaryTarget) {
			// button labels: use label from i18n or from the target (i.e. custom overlays)
			const primaryButtonLabel = labelExists(`${this.defaultBaseLabel}.primaryButton.label`)
				? i18n(`${this.defaultBaseLabel}.primaryButton.label`)
				: primaryTarget.label || '';
			if (primaryButtonLabel === '' && primaryTarget.type !== TargetTypes.LoginButton) {
				warn(`PrimaryButtonLabel "${this.defaultBaseLabel}.primaryButton.label" not found`);
			}

			// get link list entry
			const primaryLinkEntry = this.getPrimaryLinkEntry(primaryTarget, primaryButtonLabel);
			if (primaryLinkEntry) {
				linkEntries.push(primaryLinkEntry);
			}
		}

		if (secondaryTarget) {
			// button labels: use label from i18n or from the target (i.e. custom overlays)
			const secondaryButtonLabel = labelExists(`${this.defaultBaseLabel}.secondaryButton.label`)
				? i18n(`${this.defaultBaseLabel}.secondaryButton.label`)
				: secondaryTarget.label || '';

			if (secondaryButtonLabel === '') {
				warn(`SecondaryButtonLabel "${this.defaultBaseLabel}.secondaryButton.label" not found`);
			}

			// get link list entry
			const secondaryLinkEntry = this.getSecondaryLinkEntry(secondaryTarget, secondaryButtonLabel);
			if (secondaryLinkEntry) {
				linkEntries.push(secondaryLinkEntry);
			}
		}

		return linkEntries;
	};

	public getPrimaryLinkEntry = (
		primaryTarget: TargetTypePrimary,
		primaryButtonLabel: string
	): LinkListEntryType | void => {
		switch (primaryTarget.type) {
			case TargetTypes.Route:
				return {
					title: primaryButtonLabel,
					href: primaryTarget.value && primaryTarget.value.href ? primaryTarget.value.href : '',
					onClick: this.onClose.bind(this),
					openInNewWindow: false,
					iconRight: 'link-arrow',
					modifier: 'button-green',
				};
			case TargetTypes.ExternalHref:
				return {
					title: primaryButtonLabel,
					externalHref: primaryTarget.value && primaryTarget.value.href ? primaryTarget.value.href : '#',
					openInNewWindow: true,
					iconRight: 'link-arrow',
					modifier: 'button-green',
				};
			case TargetTypes.RouteAction:
				return {
					title: primaryButtonLabel,
					href: primaryTarget.value && primaryTarget.value.href ? primaryTarget.value.href : '',
					onClick: /* istanbul ignore next */ () => {
						this.onClose();
						this.onAction(
							primaryTarget && primaryTarget.value && primaryTarget.value.action
								? primaryTarget.value.action
								: ''
						);
					},
					openInNewWindow: false,
					iconRight: 'link-arrow',
					modifier: 'button-green',
				};
			case TargetTypes.Close:
				return {
					title: primaryButtonLabel,
					onClick: /* istanbul ignore next */ () => {
						this.tryCallCustomCallback(
							primaryTarget ? primaryTarget.callbackId : undefined,
							this.onClose.bind(this)
						);
					},
					openInNewWindow: false,
					iconRight: 'link-arrow',
					modifier: 'button-green',
				};
			case TargetTypes.Action:
				return {
					title: primaryButtonLabel,
					onClick: /* istanbul ignore next */ () => {
						const internalAction = this.onAction.bind(
							this,
							primaryTarget && primaryTarget.value && primaryTarget.value.action
								? primaryTarget.value.action
								: ''
						);

						this.tryCallCustomCallback(
							primaryTarget ? primaryTarget.callbackId : undefined,
							this.onClose.bind(this)
						);
						internalAction();
					},
					openInNewWindow: false,
					iconRight: 'link-arrow',
					modifier: 'button-green',
				};
			case TargetTypes.Reload:
				return {
					title: primaryButtonLabel,
					onClick: this.onReload.bind(this),
					openInNewWindow: false,
					iconRight: 'link-arrow',
					modifier: 'button-green',
				};
			case TargetTypes.LoginButton:
				return void 0;
			default:
				warn(`PrimaryTargetType "${primaryTarget.type} not found."`);
				return void 0;
		}
	};

	public getSecondaryLinkEntry = (
		secondaryTarget: TargetTypeSecondary,
		secondaryButtonLabel: string
	): LinkListEntryType | void => {
		switch (secondaryTarget.type) {
			case TargetTypes.Route:
				return {
					title: secondaryButtonLabel,
					href:
						secondaryTarget && secondaryTarget.value && secondaryTarget.value.href
							? secondaryTarget.value.href
							: '',
					onClick: this.onClose.bind(this),
					openInNewWindow: false,
					iconRight: 'link-arrow',
				};
			case TargetTypes.ExternalHref:
				return {
					title: secondaryButtonLabel,
					externalHref: i18n(`${this.defaultBaseLabel}.secondaryButton.href`),
					onClick: this.onClose.bind(this),
					openInNewWindow: true,
					iconRight: 'link-arrow',
				};
			case TargetTypes.Close:
				return {
					title: secondaryButtonLabel,
					onClick: this.onClose.bind(this),
					openInNewWindow: false,
					iconRight: 'link-arrow',
				};
			case TargetTypes.Href:
				return {
					title: secondaryButtonLabel,
					href: i18n(`${this.defaultBaseLabel}.secondaryButton.href`),
					onClick: this.onClose.bind(this),
					openInNewWindow: true,
					iconRight: 'link-arrow',
				};
			case TargetTypes.Action:
				return {
					title: secondaryButtonLabel,
					onClick: /* istanbul ignore next */ () => {
						const internalAction = this.onAction.bind(
							this,
							secondaryTarget && secondaryTarget.value && secondaryTarget.value.action
								? secondaryTarget.value.action
								: ''
						);

						this.tryCallCustomCallback(
							secondaryTarget ? secondaryTarget.callbackId : 'secondary',
							this.onClose.bind(this)
						);
						internalAction();
					},
					iconRight: 'link-arrow',
				};

			case TargetTypes.ExternalHrefWithReload:
				return {
					title: secondaryButtonLabel,
					onClick: void 0,
					externalHref: i18n(`${this.defaultBaseLabel}.secondaryButton.href`),
					openInNewWindow: false,
					iconRight: 'link-arrow',
				};
			default:
				warn(`SecondaryTargetType "${(secondaryTarget || {}).type} not found."`);
				return void 0;
		}
	};

	public render() {
		const { eventType } = this.props;

		return (
			<div>
				{eventType === EventTypes.ProgramSummary ? (
					<ProgramSummary {...this.props} />
				) : (
					this.renderNotification()
				)}
			</div>
		);
	}

	private get isDsgvo() {
		return this.props.code === EventCustomFeedbackCodes.Dsgvo;
	}
}
